import React, { createContext, ReactNode, useEffect, useState } from 'react'
import CreationToolRating from '../../../../../types/rating'
import CreationToolWatermarkPosition from '../../../../../types/watermark'
import { ICustomFont, INewCaptureInfo, IWatermark } from '../../types'

export type CreationCaptureInfoContextProps = {
    title: string
    tags: string[]
    username: string
    watermark?: IWatermark
    font?: ICustomFont
    isWatermarkOpen: boolean
    isCustomFontOpen: boolean
    previousWatermarks: IWatermark[]
    previousFonts: ICustomFont[]
    watermarkPosition: CreationToolWatermarkPosition
    sourceUrl: string
    rating: CreationToolRating
    addTags: (tags: string[]) => void
    removeTag: (tag: string) => void
    setTitle: (title: string) => void
    setUsername: (username: string) => void
    setWatermark: (watermark?: IWatermark) => void
    setFont: (font?: ICustomFont) => void
    setIsWatermarkOpen: (isWatermarkOpen: boolean) => void
    setIsCustomFontOpen: (isCustomFontOpen: boolean) => void
    setPreviousWatermarks: (watermarks: IWatermark[]) => void
    setPreviousFonts: (fonts: ICustomFont[]) => void
    setWatermarkPosition: (position: CreationToolWatermarkPosition) => void
    setSourceUrl: (url: string) => void
    setRating: (rating: CreationToolRating) => void
}

export const CreationCaptureInfoContext = createContext({} as CreationCaptureInfoContextProps)

const cleanUpTags = (tags: string[]) => tags.map(tag => tag.replace(/#/g, '').trim())

type Props = {
    children?: ReactNode
    defaultTitle?: string
    defaultTags?: string[]
    defaultUsername?: string
    defaultWatermark?: IWatermark
    defaultFont?: ICustomFont
    defaultSourceUrl?: string
    defaultPreviousWatermarks?: IWatermark[]
    defaultWatermarkPosition?: CreationToolWatermarkPosition
    defaultPreviousFonts?: ICustomFont[]
    defaultRating?: CreationToolRating
    onUpdate?: (hasChanges: boolean, info: INewCaptureInfo) => void
}

const CreationCaptureInfoManager = ({
    children,
    defaultTitle = '',
    defaultTags = [],
    defaultUsername = '',
    defaultWatermark,
    defaultFont,
    defaultSourceUrl = '',
    defaultPreviousWatermarks = [],
    defaultPreviousFonts = [],
    defaultRating = CreationToolRating.Unrated,
    defaultWatermarkPosition = CreationToolWatermarkPosition.BottomRight,
    onUpdate,
}: Props) => {
    const [title, setTitle] = useState<string>(defaultTitle)
    const [tags, setTags] = useState<string[]>(defaultTags)
    const [username, setUsername] = useState<string>(defaultUsername)
    const [watermark, setWatermark] = useState<IWatermark | undefined>(defaultWatermark)
    const [previousWatermarks, setPreviousWatermarks] = useState<IWatermark[]>(defaultPreviousWatermarks)
    const [font, setFont] = useState<ICustomFont | undefined>(defaultFont)
    const [previousFonts, setPreviousFonts] = useState<ICustomFont[]>(defaultPreviousFonts)
    const [isWatermarkOpen, setIsWatermarkOpen] = useState<boolean>(false)
    const [isCustomFontOpen, setIsCustomFontOpen] = useState<boolean>(false)
    const [sourceUrl, setSourceUrl] = useState<string>(defaultSourceUrl)
    const [rating, setRating] = useState<CreationToolRating>(defaultRating)
    const [watermarkPosition, setWatermarkPosition] = useState<CreationToolWatermarkPosition>(defaultWatermarkPosition)

    useEffect(() => {
        if (!onUpdate) return

        const hasChanges =
            watermark !== defaultWatermark ||
            font !== defaultFont ||
            title !== defaultTitle ||
            tags !== defaultTags ||
            username !== defaultUsername ||
            sourceUrl !== defaultSourceUrl ||
            rating !== defaultRating ||
            watermarkPosition !== defaultWatermarkPosition

        onUpdate(hasChanges, {
            title,
            username,
            tags,
            watermark,
            font,
            rating,
            sourceUrl,
            watermarkPosition,
        })
    }, [title, tags, username, watermark, font, rating, sourceUrl, watermarkPosition, onUpdate])

    const addTags = (newTags: string[]) => {
        if (newTags.length < 1) return

        const cleaned = cleanUpTags(newTags)
        const allTags = [...tags, ...cleaned]
        setTags(allTags.filter((tag, i) => allTags.indexOf(tag) === i))
    }

    const removeTag = (tag: string) => {
        if (!tag) return

        const cleaned = cleanUpTags([tag])
        setTags(tags => tags.filter(tag => cleaned.indexOf(tag) < 0))
    }

    return (
        <CreationCaptureInfoContext.Provider
            value={{
                title,
                tags,
                username,
                watermark,
                font,
                isWatermarkOpen,
                isCustomFontOpen,
                previousWatermarks,
                previousFonts,
                watermarkPosition,
                sourceUrl,
                rating,
                addTags,
                removeTag,
                setTitle,
                setUsername,
                setWatermark,
                setFont,
                setIsWatermarkOpen,
                setIsCustomFontOpen,
                setPreviousWatermarks,
                setPreviousFonts,
                setWatermarkPosition,
                setSourceUrl,
                setRating,
            }}
        >
            {children}
        </CreationCaptureInfoContext.Provider>
    )
}

export default CreationCaptureInfoManager
